import { Numeric, type ImperialLengthUnit, type LengthUnit, type NumericProps } from "@rothoblaas/design-system";
import type { FC, Ref } from "react";
import ImperialLengthInput from "./ImperialLengthInput";

type LengthInputProps = Omit<NumericProps, "value" | "onValueChange" | "onBlur" | "decimalScale"> & {
  // Always metric, in metricUnit: the form only ever holds metric values.
  value: number | null;
  onValueChange: (value: number | null) => void;
  onBlur?: () => void;
  ref?: Ref<HTMLInputElement>;
  // Decimals of the metric value, also what an imperial entry is rounded back to.
  decimalScale: number;
  isImperial: boolean;
  // Without both units (not a length) the field stays metric even in imperial.
  metricUnit?: LengthUnit;
  imperialUnit?: ImperialLengthUnit;
  maxDenominator?: number;
};

// A length form field's input: Numeric in metric, ImperialLengthInput in imperial. The remaining
// Numeric props only apply to the metric input.
const LengthInput: FC<LengthInputProps> = ({
  value,
  onValueChange,
  onBlur,
  ref,
  decimalScale,
  isImperial,
  metricUnit,
  imperialUnit,
  maxDenominator,
  isInvalid,
  ...numericProps
}) =>
  isImperial && metricUnit && imperialUnit ? (
    <ImperialLengthInput
      value={value}
      onValueChange={onValueChange}
      onBlur={onBlur}
      metricUnit={metricUnit}
      imperialUnit={imperialUnit}
      metricDecimalScale={decimalScale}
      maxDenominator={maxDenominator}
      isInvalid={isInvalid}
    />
  ) : (
    <Numeric
      {...numericProps}
      ref={ref}
      value={value}
      decimalScale={decimalScale}
      isInvalid={isInvalid}
      onBlur={onBlur}
      onValueChange={onValueChange}
    />
  );

export default LengthInput;
